import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { removeBusinessAsync } from '../../data-layer/business/actions';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { AlertRejectedError, confirmAlert } from '../../ui/feedback/Alert';
import { MenuItem } from '../../ui/navigation/Menu';
import { Typography } from '../../ui/typography/Typography';
import { canEditBusiness, isBloggerBusiness } from '../../utils/accessService';
import { ymTrackSettingsRemoveBusiness } from '../../utils/metrika';
import { useTranslation } from '../../utils/useTranslation';
import type { BusinessModel } from '../../data-layer/business/types';
import type { UserModel } from '../../data-layer/user/types';

type BusinessRemoveMenuItemProps = {
    business: BusinessModel;
    user?: UserModel;
    isDeclinedAdmin?: boolean;
};

export const BusinessRemoveMenuItem = ({
    business,
    user,
    isDeclinedAdmin,
}: BusinessRemoveMenuItemProps) => {
    const dispatch = useDispatch();
    const t = useTranslation();
    const navigate = useNavigate();

    const onRemove = useCallback(async () => {
        const accountPrefix = isBloggerBusiness(business.type) ? 'профиль' : 'бизнес';

        try {
            ymTrackSettingsRemoveBusiness({
                page: 'Настройки бизнеса',
                userLogin: user?.login,
                businessId: user?.profile?.publicId,
            });
            if (user && (isDeclinedAdmin || canEditBusiness(business.id, user))) {
                await confirmAlert(
                    'Удаление',
                    `Вы действительно хотите удалить cвой ${accountPrefix}?`,
                );
                await dispatch(removeBusinessAsync(business));
                navigate('/', { replace: true });
            } else {
                dispatch(showNotificationAsync(`Вы не можете удалить ${accountPrefix}`));
            }
        } catch (error) {
            if (!(error instanceof AlertRejectedError)) {
                dispatch(showNotificationAsync(`Не удалось удалить ${accountPrefix}`));
            }
        }
    }, [business, user, dispatch, navigate, isDeclinedAdmin]);

    return (
        <MenuItem onClick={onRemove}>
            <Typography color="error" noWrap>
                {t('Удалить')}
            </Typography>
        </MenuItem>
    );
};
